import { Fragment, useState, useEffect, useRef, type ChangeEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { PhoneCall, Plus, Search, Filter, Play, Pause, Square, Users, Phone, Upload, X, AlertTriangle, RefreshCw, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import { toastError } from '../../services/errorUtils'
import {
  formatDuration,
  getCallDisplayNumber,
  getStatusKind,
  getStatusLabel
} from '../../utils/callPresentation'
import type { AgentOption, BatchCallResult, BatchJobState, PhoneNumberOption } from '../../types/aicall'

interface Call {
  id: string
  agent_id: string
  agent_name?: string
  status: string
  status_kind?: string
  status_label?: string
  duration: number
  duration_label?: string
  created_at: string
  recipients?: number
  completion_rate?: number
  call_successful?: string
  transcript_summary?: string
  call_summary_title?: string
  failure_reason?: string | null
  failure_code?: string | number | null
  termination_reason?: string | null
  sip_status?: number | null
  diagnostic_hint?: string | null
  phone_number?: string | null
  display_phone_number?: string | null
  user_id?: string | null
  phone_call?: {
    agent_number?: string | null
    external_number?: string | null
    called_number?: string | null
    phone_number_id?: string | null
    call_id?: string | null
    type?: string | null
  } | null
  translated?: boolean
  message?: string | null
}

interface PaginationInfo {
  current_page: number
  total_pages: number
  total_count: number
  limit: number
  offset: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

export default function Calls() {
  const location = useLocation()
  const [calls, setCalls] = useState<Call[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberOption[]>([])
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [showNewCallModal, setShowNewCallModal] = useState(false)
  const [showBatchCallModal, setShowBatchCallModal] = useState(false)
  const [submittingCall, setSubmittingCall] = useState(false)
  const [submittingBatch, setSubmittingBatch] = useState(false)
  const [newCallData, setNewCallData] = useState({
    agentId: '',
    phoneNumber: '',
    customNumber: '',
    sourceNumber: ''
  })
  const [batchCallData, setBatchCallData] = useState({
    agentId: '',
    numbers: '',
    csvFile: null as File | null
  })
  const [batchJobId, setBatchJobId] = useState<string | null>(null)
  const [batchJobState, setBatchJobState] = useState<BatchJobState | null>(null)
  const batchPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [showCallFilters, setShowCallFilters] = useState(false)
  const [filterCallStatus, setFilterCallStatus] = useState<string>('all')
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  useEffect(() => {
    loadCalls()
    loadAgents()
    loadPhoneNumbers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const state = location.state as { openNewCall?: boolean; phoneNumber?: string } | null
    if (state?.openNewCall) {
      setNewCallData(prev => ({ ...prev, customNumber: state.phoneNumber || '' }))
      setShowNewCallModal(true)
      // Limpar o state para não reabrir se o usuário navegar de volta
      window.history.replaceState({}, '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (batchPollRef.current) {
      clearInterval(batchPollRef.current)
      batchPollRef.current = null
    }
    if (!batchJobId) return

    const poll = async () => {
      try {
        const res = await apiService.getBatchJob(batchJobId)
        const state = res.data.data as BatchJobState
        setBatchJobState(state)
        if (state.status === 'completed' || state.status === 'failed') {
          if (batchPollRef.current) clearInterval(batchPollRef.current)
          batchPollRef.current = null
          void loadCalls(1)
          if (state.status === 'completed') {
            toast.success(`Lote concluído: ${state.completed} ligações iniciadas${state.failed > 0 ? `, ${state.failed} falhas` : ''}.`)
          } else {
            toast.error(`Lote finalizado com erro: ${state.error || 'verifique as ligações.'}`)
          }
          window.setTimeout(() => {
            setShowBatchCallModal(false)
            setBatchJobId(null)
            setBatchJobState(null)
            setBatchCallData({ agentId: '', numbers: '', csvFile: null })
          }, 2000)
        }
      } catch {
        // polling errors are silent
      }
    }

    void poll()
    batchPollRef.current = setInterval(() => void poll(), 2000)
    return () => {
      if (batchPollRef.current) clearInterval(batchPollRef.current)
      batchPollRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchJobId])


  const getPhoneNumberValue = (number: PhoneNumberOption) => {
    return number.number || number.phone_number || ''
  }

  const getPhoneProviderLabel = (number: PhoneNumberOption) => {
    const providerNames: Record<string, string> = {
      clicksend: 'ClickSend',
      elevenlabs: 'ElevenLabs',
      nvoip: 'Nvoip',
      twilio: 'Twilio',
      wavoip: 'Wavoip'
    }

    return number.provider_label || providerNames[number.provider || ''] || number.provider || 'Provedor'
  }

  const getPhoneNumberLabel = (number: PhoneNumberOption) => {
    const phoneValue = getPhoneNumberValue(number)
    const name = number.label || number.name || getPhoneProviderLabel(number)
    const status = number.health && number.health !== 'healthy' ? ` (${number.health})` : ''
    return `${phoneValue || 'Número sem valor'} - ${name} - ${getPhoneProviderLabel(number)}${status}`
  }

  const getOutboundSourceNumbers = () => {
    return phoneNumbers.filter((number) => {
      const phoneValue = getPhoneNumberValue(number)
      if (!phoneValue || number.selectable === false) return false
      return number.supports_outbound !== false
    })
  }

  const loadCalls = async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      const response = await apiService.getCalls('all', 20, (page - 1) * 20)

      if (append) {
        setCalls(prev => [...prev, ...(response.data.data || [])])
      } else {
        setCalls(response.data.data || [])
      }

      if (response.data.pagination) {
        setPagination(response.data.pagination)
      }
    } catch (error) {
      toastError(error, 'Erro ao carregar ligações')
    } finally {
      if (append) {
        setLoadingMore(false)
      } else {
        setLoading(false)
      }
    }
  }

  const refreshCallsAfterProviderSettles = () => {
    void loadCalls(1)
    window.setTimeout(() => {
      void loadCalls(1)
    }, 4000)
  }

  const loadAgents = async () => {
    try {
      const response = await apiService.getAgents()
      setAgents(response.data.data || [])
    } catch (error) {
      console.error('Erro ao carregar agentes:', error)
    }
  }

  const loadPhoneNumbers = async () => {
    try {
      const response = await apiService.getPhoneNumbers()
      setPhoneNumbers(response.data.data || [])
    } catch (error) {
      console.error('Erro ao carregar números:', error)
    }
  }

  const normalizedSearchTerm = searchTerm.trim().toLowerCase()
  const filteredCalls = calls.filter(call => {
    if (filterCallStatus !== 'all' && getStatusKind(call) !== filterCallStatus) return false
    if (!normalizedSearchTerm) return true

    return [
      call.id,
      call.agent_id,
      call.agent_name,
      call.phone_number,
      call.display_phone_number,
      call.phone_call?.external_number,
      call.user_id,
      call.status,
      call.status_label
    ].some(value => String(value || '').toLowerCase().includes(normalizedSearchTerm))
  })

  // Maps status kind to design-system badge class
  const getStatusBadgeDSClass = (call: Call): string => {
    const kind = getStatusKind(call)
    switch (kind) {
      case 'success':   return 'badge badge-success'
      case 'failed':    return 'badge badge-danger'
      case 'declined':  return 'badge badge-danger opacity-75'
      case 'active':    return 'badge badge-info'
      case 'pending':   return 'badge badge-warning'
      case 'simulated': return 'badge bg-slate-100 text-slate-600'
      default:          return 'badge bg-slate-100 text-slate-500'
    }
  }

  const getStatusBadge = (call: Call) => (
    <span className={getStatusBadgeDSClass(call)}>{getStatusLabel(call)}</span>
  )

  const getFailureReason = (call: Call) => {
    if (getStatusKind(call) === 'declined') {
      return call.failure_reason || call.termination_reason || 'Chamada recusada pelo destinatário.'
    }

    return call.failure_reason || call.termination_reason || 'A chamada falhou, mas o provedor não retornou um motivo técnico detalhado.'
  }

  const getFailureMeta = (call: Call) => {
    const parts = []
    if (call.failure_code) parts.push(`código ${call.failure_code}`)
    if (call.sip_status) parts.push(`SIP ${call.sip_status}`)
    if (call.phone_call?.call_id) parts.push(call.phone_call.call_id)
    return parts.join(' | ')
  }

  const renderFailureBlock = (call: Call) => {
    const statusKind = getStatusKind(call)
    if (!['failed', 'declined'].includes(statusKind)) return null

    const isDeclined = statusKind === 'declined'
    const containerClass = isDeclined
      ? 'bg-warning-50 border-l-4 border-warning-400 rounded-r-lg p-3 text-sm text-warning-900'
      : 'bg-danger-50 border-l-4 border-danger-400 rounded-r-lg p-3 text-sm text-danger-900'
    const iconClass = isDeclined ? 'text-warning-600' : 'text-danger-600'
    const metaClass = isDeclined ? 'text-warning-700' : 'text-danger-700'

    return (
      <div className={`w-full max-w-full ${containerClass}`}>
        <div className="flex items-start gap-2">
          <AlertTriangle className={`mt-0.5 h-4 w-4 flex-none ${iconClass}`} />
          <div className="min-w-0 flex-1">
            <div className="break-words font-medium">{getFailureReason(call)}</div>
            {getFailureMeta(call) && (
              <div className={`mt-1 break-all font-mono ${metaClass}`}>{getFailureMeta(call)}</div>
            )}
            {call.diagnostic_hint && (
              <div className="mt-1 break-words text-slate-700">{call.diagnostic_hint}</div>
            )}
            {call.phone_call && (
              <div className="mt-1 break-words text-slate-500">
                Origem {call.phone_call.agent_number || 'N/A'} {'->'} destino {call.phone_call.external_number || call.phone_number || 'N/A'}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const getCallDuration = (call: Call) => call.duration_label || formatDuration(call.duration)

  const validatePhoneNumber = (phoneNumber: string) => {
    // Formato internacional: +5511999999999 ou +1234567890
    const phoneRegex = /^\+[1-9]\d{1,14}$/
    return phoneRegex.test(phoneNumber)
  }

  const formatPhoneNumber = (phoneNumber: string) => {
    // Remove todos os caracteres não numéricos exceto +
    let cleaned = phoneNumber.replace(/[^\d+]/g, '')

    // Se não começa com +, adicionar
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned
    }

    return cleaned
  }

  const handleNewCall = async () => {
    if (submittingCall) return

    try {
      setSubmittingCall(true)

      if (!newCallData.agentId || (!newCallData.phoneNumber && !newCallData.customNumber)) {
        toast.error('Selecione um agente e um número de telefone')
        return
      }

      const phoneNumber = newCallData.customNumber || newCallData.phoneNumber
      const formattedNumber = formatPhoneNumber(phoneNumber)

      if (!validatePhoneNumber(formattedNumber)) {
        toast.error('Use o formato internacional: +[código do país][número], ex: +5511999999999')
        return
      }

      const selectedSourceNumber = phoneNumbers.find((number) => number.id === newCallData.sourceNumber)
      const selectedProviderLabel = selectedSourceNumber ? getPhoneProviderLabel(selectedSourceNumber) : 'provedor selecionado'

      const response = await apiService.makeCall({
        agentId: newCallData.agentId,
        phone_number: formattedNumber,
        from_number: selectedSourceNumber ? getPhoneNumberValue(selectedSourceNumber) : undefined,
        provider: selectedSourceNumber?.provider,
        provider_connection_id: selectedSourceNumber?.provider_connection_id,
        source_phone_number_id: selectedSourceNumber?.id
      })

      const createdCall = response.data.data as Call | undefined
      const callId = createdCall?.id
      const createdStatusKind = createdCall ? getStatusKind(createdCall) : undefined
      if (createdStatusKind === 'failed') {
        toast.error(createdCall?.failure_reason || response.data.data?.message || 'Ligação falhou no provedor SIP', { duration: 8000 })
      } else if (createdStatusKind === 'declined') {
        toast(createdCall?.diagnostic_hint || createdCall?.message || 'Chamada recusada pelo destinatário.', { duration: 8000 })
      } else {
        toast.success(`Ligação enviada via ${selectedProviderLabel}${callId ? ` (${callId})` : ''}. Se falhar, o motivo aparece na lista.`)
      }
      setShowNewCallModal(false)
      setNewCallData({ agentId: '', phoneNumber: '', customNumber: '', sourceNumber: '' })
      refreshCallsAfterProviderSettles()
    } catch (error) {
      toastError(error, 'Erro ao iniciar ligação')
      refreshCallsAfterProviderSettles()
    } finally {
      setSubmittingCall(false)
    }
  }

  const closeBatchModal = () => {
    setShowBatchCallModal(false)
    setBatchJobId(null)
    setBatchJobState(null)
    setBatchCallData({ agentId: '', numbers: '', csvFile: null })
  }

  const handleBatchCall = async () => {
    if (submittingBatch) return

    try {
      setSubmittingBatch(true)

      if (!batchCallData.agentId) {
        toast.error('Selecione um agente')
        return
      }

      let numbers: string[] = []

      if (batchCallData.csvFile) {
        const text = await batchCallData.csvFile.text()
        numbers = text.split('\n').map(line => line.trim()).filter(line => line)
      } else if (batchCallData.numbers) {
        numbers = batchCallData.numbers
          .split(/[,\n]+/)
          .map(num => num.trim())
          .filter(num => num)
      }

      if (numbers.length === 0) {
        toast.error('Adicione números para ligação em lote')
        return
      }

      const validNumbers: string[] = []
      const invalidNumbers: string[] = []

      for (const number of numbers) {
        const formattedNumber = formatPhoneNumber(number)
        if (validatePhoneNumber(formattedNumber)) {
          validNumbers.push(formattedNumber)
        } else {
          invalidNumbers.push(number)
        }
      }

      if (invalidNumbers.length > 0) {
        toast.error(`${invalidNumbers.length} números inválidos encontrados. Verifique o formato.`)
        return
      }

      if (validNumbers.length === 0) {
        toast.error('Nenhum número válido encontrado')
        return
      }

      const response = await apiService.createBatchCalls({
        agentId: batchCallData.agentId,
        phone_numbers: validNumbers
      })

      const batchId = response.data.data?.batch_id as string | undefined
      if (batchId) {
        setBatchJobId(batchId)
      } else {
        // fallback para resposta no formato antigo
        const batchCalls = response.data.data?.calls as BatchCallResult[] | undefined
        const hasRealCalls = batchCalls?.some((c) => c.is_real_call) ?? false
        toast.success(`${validNumbers.length} ligações ${hasRealCalls ? 'reais' : 'simuladas'} em lote iniciadas!`)
        closeBatchModal()
        try {
          refreshCallsAfterProviderSettles()
        } catch (refreshError) {
          toastError(refreshError, 'Erro ao atualizar lista de ligações')
        }
      }
    } catch (error) {
      toastError(error, 'Erro ao iniciar ligações em lote')
    } finally {
      setSubmittingBatch(false)
    }
  }

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'text/csv') {
      setBatchCallData({ ...batchCallData, csvFile: file })
      toast.success('Arquivo CSV carregado com sucesso!')
    } else {
      toast.error('Por favor, selecione um arquivo CSV válido')
    }
  }

  const handlePauseCall = async (callId: string) => {
    if (actionLoading) return
    setActionLoading(callId)
    try {
      await apiService.pauseCall(callId)
      toast.success('Ligação pausada com sucesso')
      void loadCalls(1)
    } catch (error) {
      toastError(error, 'Erro ao pausar ligação')
    } finally {
      setActionLoading(null)
    }
  }

  const handleEndCall = async (callId: string) => {
    if (actionLoading) return
    setActionLoading(callId)
    try {
      await apiService.endCall(callId)
      toast.success('Ligação encerrada com sucesso')
      void loadCalls(1)
    } catch (error) {
      toastError(error, 'Erro ao encerrar ligação')
    } finally {
      setActionLoading(null)
    }
  }

  const handleStartCall = async (callId: string) => {
    if (actionLoading) return
    setActionLoading(callId)
    try {
      await apiService.startCall(callId)
      toast.success('Ligação iniciada com sucesso')
      void loadCalls(1)
    } catch (error) {
      toastError(error, 'Erro ao iniciar ligação')
    } finally {
      setActionLoading(null)
    }
  }

  const activeFilterCount = filterCallStatus !== 'all' ? 1 : 0

  // Loading skeleton
  if (loading) {
    return (
      <div className="page-shell">
        {/* Skeleton header */}
        <div className="page-header">
          <div>
            <div className="h-8 w-32 animate-pulse rounded-lg bg-slate-200" />
            <div className="mt-2 h-4 w-64 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-36 animate-pulse rounded-lg bg-slate-200" />
            <div className="h-10 w-44 animate-pulse rounded-lg bg-slate-200" />
          </div>
        </div>
        {/* Skeleton metrics */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="metric-card flex items-center gap-3">
              <div className="metric-icon animate-pulse bg-slate-100" />
              <div className="space-y-2">
                <div className="h-3 w-20 animate-pulse rounded bg-slate-100" />
                <div className="h-6 w-12 animate-pulse rounded bg-slate-200" />
              </div>
            </div>
          ))}
        </div>
        {/* Skeleton table */}
        <div className="surface">
          <div className="panel-header">
            <div className="h-5 w-40 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="p-4 sm:p-5">
            <div className="table-shell">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="table-head">
                  <tr>
                    {['ID', 'Agente', 'Destino', 'Status', 'Duração', 'Criada em', 'Ações'].map(h => (
                      <th key={h} className="px-6 py-3 text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 animate-pulse rounded bg-slate-100" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Metrics derived from loaded data
  const totalCalls = pagination?.total_count ?? calls.length
  const successCount = calls.filter(c => getStatusKind(c) === 'success').length
  const activeCount = calls.filter(c => ['active', 'pending'].includes(getStatusKind(c))).length
  const failedCount = calls.filter(c => ['failed', 'declined'].includes(getStatusKind(c))).length

  return (
    <div className="page-shell">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Ligações</h1>
          <p className="page-description">
            Gerencie suas ligações e chamadas em lote
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { loadAgents(); setShowNewCallModal(true) }}
            className="btn-primary"
          >
            <PhoneCall className="h-4 w-4" />
            Nova Ligação
          </button>
          <button
            onClick={() => { loadAgents(); setShowBatchCallModal(true) }}
            className="btn-secondary"
          >
            <Users className="h-4 w-4" />
            Lote
          </button>
        </div>
      </div>

      {/* Metrics row */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="metric-card">
          <div className="flex items-center gap-3">
            <div className="metric-icon bg-primary-50 text-primary-600">
              <PhoneCall className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Total</p>
              <p className="text-2xl font-bold tabular-nums text-slate-950">{totalCalls}</p>
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-center gap-3">
            <div className="metric-icon bg-success-50 text-success-600">
              <CheckCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Concluídas</p>
              <p className="text-2xl font-bold tabular-nums text-slate-950">{successCount}</p>
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-center gap-3">
            <div className="metric-icon bg-warning-50 text-warning-600">
              <Clock className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Em andamento</p>
              <p className="text-2xl font-bold tabular-nums text-slate-950">{activeCount}</p>
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-center gap-3">
            <div className="metric-icon bg-danger-50 text-danger-600">
              <XCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Falhas</p>
              <p className="text-2xl font-bold tabular-nums text-slate-950">{failedCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters toolbar */}
      <div className="toolbar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            aria-label="Buscar ligações"
            placeholder="Buscar ligações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
        <button
          className={`btn-secondary relative ${showCallFilters ? 'bg-primary-50 text-primary-700 border-primary-200' : ''}`}
          onClick={() => setShowCallFilters(v => !v)}
        >
          <Filter className="h-4 w-4" />
          Filtros
          {activeFilterCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {showCallFilters && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-600">Status</label>
            <select
              value={filterCallStatus}
              onChange={(e) => setFilterCallStatus(e.target.value)}
              className="input h-8 w-auto py-0 text-sm"
            >
              <option value="all">Todos</option>
              <option value="success">Concluídas</option>
              <option value="failed">Falhou</option>
              <option value="declined">Recusadas</option>
              <option value="active">Ativas</option>
              <option value="pending">Em andamento</option>
              <option value="simulated">Simuladas</option>
            </select>
          </div>
          {filterCallStatus !== 'all' && (
            <button
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
              onClick={() => setFilterCallStatus('all')}
            >
              <X className="h-3 w-3" />Limpar
            </button>
          )}
        </div>
      )}

      {/* Calls list */}
      <div className="surface">
        <div className="panel-header">
          <div>
            <h3 className="panel-title">
              Ligações ({pagination?.total_count || filteredCalls.length})
            </h3>
            <p className="panel-subtitle">Histórico com status, duração e diagnóstico técnico de falha.</p>
          </div>
          <div className="flex items-center gap-3">
            {pagination && (
              <span className="text-sm text-slate-500">
                Página {pagination.current_page} de {pagination.total_pages}
              </span>
            )}
            <button
              type="button"
              onClick={() => loadCalls(1)}
              disabled={loading}
              className="btn-secondary"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {filteredCalls.length === 0 ? (
            <div className="empty-state">
              <PhoneCall className="h-12 w-12 text-slate-400 mb-4" />
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Nenhuma ligação encontrada</h3>
              <p className="text-sm text-slate-500">
                {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece fazendo sua primeira ligação.'}
              </p>
            </div>
          ) : (
            <div>
              {/* Mobile cards */}
              <div className="space-y-3 md:hidden">
                {filteredCalls.map((call) => (
                  <div key={call.id} className="card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">{call.id}</p>
                        <p className="mt-1 truncate text-sm text-slate-600">{getCallDisplayNumber(call)}</p>
                      </div>
                      <div className="flex-shrink-0">{getStatusBadge(call)}</div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs font-medium uppercase text-slate-500">Agente</p>
                        <p className="mt-1 truncate text-slate-900">{call.agent_name || call.agent_id}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase text-slate-500">Duração</p>
                        <p className="mt-1 text-slate-900">{getCallDuration(call)}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs font-medium uppercase text-slate-500">Criada em</p>
                        <p className="mt-1 text-slate-900">{new Date(call.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                    </div>

                    <div className="mt-3">{renderFailureBlock(call)}</div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="table-shell hidden md:block">
                <table className="min-w-[64rem] w-full divide-y divide-slate-200">
                  <thead className="table-head">
                    <tr>
                      <th className="px-6 py-3 text-left">ID</th>
                      <th className="px-6 py-3 text-left">Agente</th>
                      <th className="px-6 py-3 text-left">Destino</th>
                      <th className="px-6 py-3 text-left">Status</th>
                      <th className="px-6 py-3 text-left">Duração</th>
                      <th className="px-6 py-3 text-left">Criada em</th>
                      <th className="px-6 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {filteredCalls.map((call) => (
                      <Fragment key={call.id}>
                        <tr className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="max-w-[10rem] truncate text-sm font-medium text-slate-900">{call.id}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-[12rem] truncate text-sm text-slate-900">{call.agent_name || call.agent_id}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-[14rem] truncate font-mono text-sm text-slate-900">
                              {getCallDisplayNumber(call)}
                            </div>
                          </td>
                          <td className="px-6 py-4 align-top">
                            {getStatusBadge(call)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {getCallDuration(call)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                            {new Date(call.created_at).toLocaleString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1">
                              {getStatusKind(call) === 'active' && (
                                <>
                                  <button
                                    className={`btn-icon text-warning-600 ${actionLoading === call.id ? 'opacity-50 cursor-wait' : ''}`}
                                    aria-label="Pausar ligação"
                                    disabled={actionLoading === call.id}
                                    onClick={() => handlePauseCall(call.id)}
                                    title="Pausar ligação"
                                  >
                                    {actionLoading === call.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
                                  </button>
                                  <button
                                    className={`btn-icon text-danger-600 ${actionLoading === call.id ? 'opacity-50 cursor-wait' : ''}`}
                                    aria-label="Encerrar ligação"
                                    disabled={actionLoading === call.id}
                                    onClick={() => handleEndCall(call.id)}
                                    title="Encerrar ligação"
                                  >
                                    {actionLoading === call.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                                  </button>
                                </>
                              )}
                              {getStatusKind(call) === 'pending' && (
                                <button
                                  className={`btn-icon text-success-600 ${actionLoading === call.id ? 'opacity-50 cursor-wait' : ''}`}
                                  aria-label="Iniciar ligação"
                                  disabled={actionLoading === call.id}
                                  onClick={() => handleStartCall(call.id)}
                                  title="Iniciar ligação"
                                >
                                  {actionLoading === call.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {['failed', 'declined'].includes(getStatusKind(call)) && (
                          <tr className="bg-white">
                            <td colSpan={7} className="px-6 pb-4 pt-0">
                              <div className="max-w-4xl">
                                {renderFailureBlock(call)}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex items-center justify-center py-4 gap-2 text-sm text-slate-500">
                  <RefreshCw className="h-4 w-4 animate-spin text-primary-500" />
                  Carregando mais ligações...
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.total_pages > 1 && (
                <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-1">
                    {/* Prev */}
                    <button
                      onClick={() => loadCalls(pagination.prev_page || 1)}
                      disabled={!pagination.has_prev_page || loading}
                      className="btn-icon"
                      aria-label="Página anterior"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    {/* Page numbers */}
                    {(() => {
                      const { total_pages, current_page } = pagination

                      if (total_pages <= 7) {
                        return Array.from({ length: total_pages }, (_, i) => i + 1).map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => loadCalls(pageNum)}
                            disabled={loading}
                            className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition ${
                              pageNum === current_page
                                ? 'bg-primary-600 text-white'
                                : 'text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        ))
                      }

                      const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = []
                      pages.push(1, 2)

                      const windowStart = Math.max(3, current_page - 1)
                      const windowEnd = Math.min(total_pages - 2, current_page + 1)

                      if (windowStart > 3) pages.push('ellipsis-start')
                      for (let p = windowStart; p <= windowEnd; p++) pages.push(p)
                      if (windowEnd < total_pages - 2) pages.push('ellipsis-end')

                      pages.push(total_pages - 1, total_pages)

                      const seen = new Set<number | string>()
                      const dedupedPages = pages.filter((p) => {
                        if (typeof p === 'number') {
                          if (seen.has(p)) return false
                          seen.add(p)
                          return true
                        }
                        return true
                      })

                      return dedupedPages.map((p, idx) => {
                        if (p === 'ellipsis-start' || p === 'ellipsis-end') {
                          return (
                            <span key={`${p}-${idx}`} className="px-1 py-2 text-sm text-slate-400 select-none">
                              &hellip;
                            </span>
                          )
                        }
                        return (
                          <button
                            key={p}
                            onClick={() => loadCalls(p)}
                            disabled={loading}
                            className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition ${
                              p === current_page
                                ? 'bg-primary-600 text-white'
                                : 'text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            {p}
                          </button>
                        )
                      })
                    })()}

                    {/* Next */}
                    <button
                      onClick={() => loadCalls(pagination.next_page || pagination.total_pages)}
                      disabled={!pagination.has_next_page || loading}
                      className="btn-icon"
                      aria-label="Próxima página"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  <p className="text-sm text-slate-500">
                    Mostrando {((pagination.current_page - 1) * pagination.limit) + 1}–{Math.min(pagination.current_page * pagination.limit, pagination.total_count)} de {pagination.total_count} ligações
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Nova Ligação */}
      {showNewCallModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-6 text-center sm:p-0">
            <div className="modal-backdrop" onClick={() => setShowNewCallModal(false)} />
            <div className="modal-panel relative w-full max-w-lg">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Nova Ligação
                  </h3>
                  <button
                    onClick={() => setShowNewCallModal(false)}
                    className="btn-icon"
                    aria-label="Fechar modal de nova ligação"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Agente
                    </label>
                    <select
                      value={newCallData.agentId}
                      onChange={(e) => setNewCallData({ ...newCallData, agentId: e.target.value })}
                      className="input"
                    >
                      <option value="">Selecione um agente</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name || agent.id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Origem da chamada
                    </label>
                    <select
                      value={newCallData.sourceNumber}
                      onChange={(e) => setNewCallData({ ...newCallData, sourceNumber: e.target.value })}
                      className="input"
                    >
                      <option value="">Usar origem configurada no agente</option>
                      {getOutboundSourceNumbers().map((number) => (
                        <option key={`source-${number.id}`} value={number.id}>
                          {getPhoneNumberLabel(number)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Destino da ligação
                    </label>
                    <select
                      value={newCallData.phoneNumber}
                      onChange={(e) => setNewCallData({ ...newCallData, phoneNumber: e.target.value })}
                      className="input mb-2"
                    >
                      <option value="">Selecione um número</option>
                      {phoneNumbers.map((number) => (
                        <option
                          key={`destination-${number.id}`}
                          value={getPhoneNumberValue(number)}
                          disabled={number.selectable === false || !getPhoneNumberValue(number)}
                        >
                          {getPhoneNumberLabel(number)}
                        </option>
                      ))}
                    </select>
                    <div className="py-1 text-center text-xs font-medium text-slate-400">ou</div>
                    <input
                      type="tel"
                      placeholder="+5511999999999"
                      value={newCallData.customNumber}
                      onChange={(e) => setNewCallData({ ...newCallData, customNumber: e.target.value })}
                      className="input mt-2"
                    />
                    <p className="mt-1.5 text-xs text-slate-500">
                      Formato: +5511999999999 (código do país + DDD + número)
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowNewCallModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleNewCall}
                  disabled={submittingCall}
                >
                  {submittingCall ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <PhoneCall className="h-4 w-4" />
                  )}
                  {submittingCall ? 'Iniciando...' : 'Iniciar Ligação'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ligações em Lote */}
      {showBatchCallModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-6 text-center sm:p-0">
            <div className="modal-backdrop" onClick={closeBatchModal} />
            <div className="modal-panel relative w-full max-w-lg">

              {/* Fase 1: Formulário */}
              {!batchJobId && (
                <>
                  {/* Step indicator */}
                  <div className="border-b border-slate-100 px-6 pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Ligações em Lote</h3>
                        <p className="mt-0.5 text-xs text-slate-500">Passo 1 de 2 — Configurar</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">1</span>
                          <div className="h-px w-6 bg-slate-200" />
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-400">2</span>
                        </div>
                        <button onClick={closeBatchModal} className="btn-icon" aria-label="Fechar modal de ligações em lote">
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-5">
                    <div className="space-y-4">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">
                          Agente
                        </label>
                        <select
                          value={batchCallData.agentId}
                          onChange={(e) => setBatchCallData({ ...batchCallData, agentId: e.target.value })}
                          className="input"
                        >
                          <option value="">Selecione um agente</option>
                          {agents.map((agent) => (
                            <option key={agent.id} value={agent.id}>{agent.name || agent.id}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">
                          <span className="flex items-center gap-1.5"><Upload className="h-4 w-4" />Arquivo CSV</span>
                        </label>
                        <input type="file" accept=".csv" onChange={handleFileUpload} className="input" />
                        <p className="mt-1.5 text-xs text-slate-500">Formato: um número por linha</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="flex-1 border-t border-slate-200" />
                        <span className="text-xs font-medium text-slate-400">ou</span>
                        <div className="flex-1 border-t border-slate-200" />
                      </div>

                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">
                          <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />Números (separados por vírgula ou linha)</span>
                        </label>
                        <textarea
                          placeholder={"+5511999999999\n+5511888888888\n+5511777777777"}
                          value={batchCallData.numbers}
                          onChange={(e) => setBatchCallData({ ...batchCallData, numbers: e.target.value })}
                          className="input h-24 resize-none"
                          rows={3}
                        />
                        <p className="mt-1.5 text-xs text-slate-500">
                          Formato: +5511999999999. Um por linha ou separados por vírgula.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
                    <button type="button" className="btn-secondary" onClick={closeBatchModal}>Cancelar</button>
                    <button type="button" className="btn-primary" onClick={handleBatchCall} disabled={submittingBatch}>
                      {submittingBatch ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      {submittingBatch ? 'Criando lote...' : 'Iniciar Ligações em Lote'}
                    </button>
                  </div>
                </>
              )}

              {/* Fase 2: Progresso */}
              {batchJobId && (
                <>
                  <div className="border-b border-slate-100 px-6 pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {batchJobState?.status === 'completed' ? 'Lote Concluído' : batchJobState?.status === 'failed' ? 'Lote com Erro' : 'Processando Lote...'}
                        </h3>
                        <p className="mt-0.5 text-xs text-slate-500">Passo 2 de 2 — Progresso</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-success-100 text-xs font-bold text-success-700">1</span>
                          <div className="h-px w-6 bg-primary-300" />
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">2</span>
                        </div>
                        <button onClick={closeBatchModal} className="btn-icon" aria-label="Fechar modal de progresso do lote">
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-5">
                    {!batchJobState ? (
                      <div className="flex items-center justify-center py-8 gap-2 text-sm text-slate-500">
                        <RefreshCw className="h-5 w-5 animate-spin text-primary-600" />
                        Aguardando resposta...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Progress bar */}
                        <div>
                          <div className="flex justify-between text-sm text-slate-600 mb-2">
                            <span>{batchJobState.completed + batchJobState.failed} de {batchJobState.total} processadas</span>
                            <span className="font-semibold">
                              {batchJobState.total > 0
                                ? Math.round(((batchJobState.completed + batchJobState.failed) / batchJobState.total) * 100)
                                : 0}%
                            </span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-slate-200">
                            <div
                              className={`h-2 rounded-full transition-all duration-500 ${
                                batchJobState.failed > 0 && batchJobState.completed === 0
                                  ? 'bg-danger-500'
                                  : batchJobState.status === 'completed'
                                    ? 'bg-success-500'
                                    : 'bg-primary-500'
                              }`}
                              style={{
                                width: `${batchJobState.total > 0
                                  ? ((batchJobState.completed + batchJobState.failed) / batchJobState.total) * 100
                                  : 0}%`
                              }}
                            />
                          </div>
                        </div>

                        {/* Counters */}
                        <div className="grid grid-cols-4 gap-2 text-center text-xs">
                          <div className="rounded-lg bg-slate-50 px-2 py-2">
                            <p className="text-base font-bold tabular-nums text-slate-700">{batchJobState.pending}</p>
                            <p className="text-slate-500">Aguardando</p>
                          </div>
                          <div className="rounded-lg bg-primary-50 px-2 py-2">
                            <p className="text-base font-bold tabular-nums text-primary-700">{batchJobState.in_progress}</p>
                            <p className="text-primary-500">Em curso</p>
                          </div>
                          <div className="rounded-lg bg-success-50 px-2 py-2">
                            <p className="text-base font-bold tabular-nums text-success-700">{batchJobState.completed}</p>
                            <p className="text-success-600">Concluídas</p>
                          </div>
                          <div className="rounded-lg bg-danger-50 px-2 py-2">
                            <p className="text-base font-bold tabular-nums text-danger-700">{batchJobState.failed}</p>
                            <p className="text-danger-500">Falhas</p>
                          </div>
                        </div>

                        {/* Call list */}
                        <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 scrollbar-thin">
                          {batchJobState.calls.map((call, i) => (
                            <div key={i} className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50 transition-colors">
                              <span className="flex-none">
                                {call.status === 'completed' && <CheckCircle className="h-4 w-4 text-success-600" />}
                                {call.status === 'failed' && <XCircle className="h-4 w-4 text-danger-600" />}
                                {call.status === 'processing' && <RefreshCw className="h-4 w-4 animate-spin text-primary-500" />}
                                {call.status === 'pending' && <Clock className="h-4 w-4 text-slate-400" />}
                              </span>
                              <span className="flex-1 truncate text-sm font-mono text-slate-800">{call.phone_number}</span>
                              <span className={`flex-none text-xs font-medium ${
                                call.status === 'completed' ? 'text-success-600' :
                                call.status === 'failed' ? 'text-danger-600' :
                                call.status === 'processing' ? 'text-primary-600' :
                                'text-slate-400'
                              }`}>
                                {call.status === 'completed' ? 'Iniciada' :
                                 call.status === 'failed' ? (call.error?.slice(0, 30) || 'Falhou') :
                                 call.status === 'processing' ? 'Ligando...' :
                                 'Aguardando'}
                              </span>
                            </div>
                          ))}
                        </div>

                        {batchJobState.error && (
                          <p className="rounded-lg bg-danger-50 border border-danger-100 px-3 py-2 text-xs text-danger-700">{batchJobState.error}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
                    <button type="button" className="btn-secondary" onClick={closeBatchModal}>Fechar</button>
                    {batchJobState?.status === 'completed' && (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                          closeBatchModal()
                          void loadCalls(1)
                        }}
                      >
                        <PhoneCall className="h-4 w-4" />
                        Ver Ligações
                      </button>
                    )}
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  )
}
